import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { MapPin, Sparkles, Search, SlidersHorizontal } from "lucide-react";
import { StarRating } from "@/components/StarRating";

const inputCls = "px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717] text-[#F7ECEB] text-sm";

const Card = ({ p, founder }) => (
  <Link to={`/practitioners/${p.id}`} className="tt-card p-6 block" data-testid={`practitioner-card-${p.id}`}>
    <div className="aspect-[4/5] rounded-xl overflow-hidden bg-[#110D0D]">
      {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />}
    </div>
    <div className="mt-5">
      {founder && (
        <div className="inline-block text-xs uppercase tracking-[0.2em] bg-[#E58B5E] text-white px-3 py-1 rounded-full mb-3">Founder</div>
      )}
      <h3 className="font-serif text-2xl text-[#F7ECEB]">{p.name}</h3>
      <div className="text-sm text-[#C93B3B] mt-1">{p.title}</div>
      <div className="flex items-center gap-2 text-xs text-[#A89896] mt-3">
        <MapPin className="w-3 h-3" /> {p.location}
        <span className="mx-1">·</span> {p.years_experience} yrs
        {p.gender && <><span className="mx-1">·</span> {p.gender}</>}
      </div>
      <div className="mt-3"><StarRating value={p.avg_rating} count={p.review_count} size="w-3.5 h-3.5" testId={`rating-${p.id}`} /></div>
      <div className="mt-3 flex flex-wrap gap-1">
        {(p.session_types || []).map((s) => (
          <span key={s} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#382A2A] text-[#A89896]">{s}</span>
        ))}
      </div>
      <div className="mt-4 text-sm text-[#A89896]">
        {founder ? `$${p.hourly_rate}/hr · 10-session package $${p.package_price}` : `from $${p.hourly_rate}/hr`}
      </div>
    </div>
  </Link>
);

export default function Practitioners() {
  const [practitioners, setPractitioners] = useState([]);
  const [filters, setFilters] = useState({ q: "", gender: "", session_type: "", max_rate: "", sort: "" });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ""));
    const t = setTimeout(() => {
      api.get("/practitioners", { params }).then((r) => { setPractitioners(r.data); setLoaded(true); });
    }, 250);
    return () => clearTimeout(t);
  }, [filters]);

  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });
  const founders = practitioners.filter((p) => p.is_founder);
  const cuddlers = practitioners.filter((p) => !p.is_founder);
  const filtering = Object.values(filters).some((v) => v !== "");

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="practitioners-page">
      <div className="text-xs uppercase tracking-[0.28em] text-[#C93B3B]">Our practitioners</div>
      <h1 className="font-serif text-5xl lg:text-6xl mt-4 text-[#F7ECEB]">Held by care. Chosen by intention.</h1>
      <p className="text-[#A89896] mt-6 max-w-2xl">
        Intimacy coaching is offered exclusively by our founder. Certified cuddle therapists are vetted with a rigorous, unhurried process.
      </p>

      <div className="mt-10 tt-card p-4 flex flex-wrap items-center gap-3" data-testid="practitioner-filters">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#A89896]" />
          <input value={filters.q} onChange={set("q")} placeholder="City, state, name or specialty" data-testid="filter-q"
            className={`${inputCls} w-full`} />
        </div>
        <SlidersHorizontal className="w-4 h-4 text-[#A89896] hidden md:block" />
        <select value={filters.gender} onChange={set("gender")} data-testid="filter-gender" className={inputCls}>
          <option value="">Any gender</option>
          <option value="Female">Female</option>
          <option value="Male">Male</option>
          <option value="Non-binary">Non-binary</option>
        </select>
        <select value={filters.session_type} onChange={set("session_type")} data-testid="filter-session-type" className={inputCls}>
          <option value="">In-person or virtual</option>
          <option value="In-Person">In-Person</option>
          <option value="Virtual">Virtual</option>
        </select>
        <select value={filters.max_rate} onChange={set("max_rate")} data-testid="filter-max-rate" className={inputCls}>
          <option value="">Any rate</option>
          <option value="80">Up to $80/hr</option>
          <option value="100">Up to $100/hr</option>
          <option value="150">Up to $150/hr</option>
          <option value="250">Up to $250/hr</option>
        </select>
        <select value={filters.sort} onChange={set("sort")} data-testid="filter-sort" className={inputCls}>
          <option value="">Sort: featured</option>
          <option value="rating">Top rated</option>
          <option value="rate_asc">Price: low to high</option>
          <option value="rate_desc">Price: high to low</option>
        </select>
        {filtering && (
          <button onClick={() => setFilters({ q: "", gender: "", session_type: "", max_rate: "", sort: "" })}
            className="text-sm text-[#E58B5E] hover:underline" data-testid="filter-clear">Clear</button>
        )}
      </div>

      {founders.length === 0 && !filtering && (
        <Link to="/membership" className="mt-8 inline-flex items-center gap-2 text-sm text-[#E58B5E] hover:underline" data-testid="founder-locked-note">
          <Sparkles className="w-4 h-4" /> Founder intimacy coaching is available to members — view membership
        </Link>
      )}

      {founders.length > 0 && (
        <div className="mt-12">
          <div className="text-xs uppercase tracking-[0.28em] text-[#E58B5E] mb-6">Founder Coaching</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {founders.map((p) => <Card key={p.id} p={p} founder />)}
          </div>
        </div>
      )}

      <div className="mt-16">
        <div className="text-xs uppercase tracking-[0.28em] text-[#C93B3B] mb-6">
          Certified Cuddle Therapists {loaded && <span className="text-[#A89896] normal-case tracking-normal">· {cuddlers.length} found</span>}
        </div>
        {cuddlers.length === 0 ? (
          <div className="tt-card p-12 text-center" data-testid="no-cuddlers">
            <Sparkles className="w-8 h-8 text-[#E58B5E] mx-auto" strokeWidth={1.4} />
            <h3 className="font-serif text-3xl mt-6 text-[#F7ECEB]">{filtering ? "No practitioners match those filters." : "Vetted practitioners — coming soon."}</h3>
            <p className="text-[#A89896] mt-4 max-w-xl mx-auto leading-relaxed">
              {filtering ? "Try widening your search — a different city, rate, or session type." : "We are hand-selecting cuddle therapists with a rigorous, unhurried vetting process. Join the membership to be notified when new practitioners become available."}
            </p>
            {!filtering && (
              <Link to="/membership" className="btn-primary inline-block mt-8" data-testid="cuddle-cta-membership">Join via membership</Link>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cuddlers.map((p) => <Card key={p.id} p={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
